"use client";

import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  X,
  Clock,
  Tag,
  Plus,
  Loader2,
  Check,
  Home,
  Car,
  Briefcase,
  Trash2,
  AlertTriangle,
  AlertCircle,
} from "lucide-react";
import {
  FaDollarSign,
  FaEuroSign,
  FaPoundSign,
  FaRubleSign,
  FaShekelSign,
} from "react-icons/fa";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import EditServiceModal from "./EditServiceModal";
import { motion, AnimatePresence } from "framer-motion";
import CreateServiceBussiness from "./CreateServiceBussiness";
import ShowWorkersModal from "./ShowWorkersModal";
import { HiOutlineUserGroup } from "react-icons/hi2";
import AddListingModal from "./../../Auth/MyServices/AddListingModal";
import EditListingModal from "../../Auth/MyServices/EditListingModal";
import VehicleCard from "./Dealerships/VehicleCard";
import VehicleItemSkeleton from "./Dealerships/VehicleItemSkeleton";
import VehicleModal from "./Dealerships/VehicleModal";
import {
  getVehicles,
  deleteVehicle,
} from "../../api/dealership/dealershpService";
import { REST_API_URL } from "../../api/config";
import { buildHeaders } from "../../api/buildHeaders";
import { getCurrencySymbol } from "../../utils/currency/getCurrencySymbol";
import { smartDuration } from "../../utils/duration/smartDuration.ts";
import { useAppSelector } from "../../hooks/redux/useRedux";
import { selectIndustryData } from "../../redux/slices/profileSlice/ProfileSlice";
import { PrimaryButton } from "../../components/buttons/primaryButton/PrimaryButton";
const API_URL = REST_API_URL;

const CURRENCY_ICON_MAP = {
  USD: FaDollarSign,
  EUR: FaEuroSign,
  GBP: FaPoundSign,
  RUB: FaRubleSign,
  ILS: FaShekelSign,
  AUD: FaDollarSign,
  CAD: FaDollarSign,
  BRL: FaDollarSign,
  MXN: FaDollarSign,
  AED: FaDollarSign,
  ARS: FaDollarSign,
  ZAR: FaDollarSign,
};

function DeleteConfirmationModal({ isOpen, onClose, onConfirm, listingName }) {
  const { t } = useTranslation();
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] px-4"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
          >
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mb-4">
                <AlertTriangle className="w-8 h-8 text-red-500" />
              </div>

              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {t("home.deleteListingTitle")}
              </h3>

              <p className="text-gray-600 mb-6">
                {t("home.deleteListingPrompt")}
              </p>

              <div className="flex gap-3 w-full">
                <PrimaryButton
                  onClick={onClose}
                  variant="outline"
                  size="small"
                  className="flex-1"
                  label={t("home.cancel")}
                />
                <PrimaryButton
                  onClick={onConfirm}
                  size="small"
                  className="flex-1 bg-red-500 hover:bg-red-600"
                  label={t("home.delete")}
                />
              </div>

              <p className="text-xs text-gray-500 mt-4">
                {t("home.deleteListingWarning")}
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function DeleteServiceConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  serviceName,
}) {
  const { t } = useTranslation();
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] px-4"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
          >
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mb-4">
                <AlertTriangle className="w-8 h-8 text-red-500" />
              </div>

              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {t("home.deleteServiceTitle")}
              </h3>

              <p
                className="text-gray-600 mb-6"
                dangerouslySetInnerHTML={{
                  __html: t("home.deleteServicePrompt", {
                    serviceName: serviceName || "this service",
                  }),
                }}
              />

              <div className="flex gap-3 w-full">
                <PrimaryButton
                  onClick={onClose}
                  variant="outline"
                  size="small"
                  className="flex-1"
                  label={t("home.cancel")}
                />
                <PrimaryButton
                  onClick={onConfirm}
                  size="small"
                  className="flex-1 bg-red-500 hover:bg-red-600"
                  label={t("home.delete")}
                />
              </div>

              <p className="text-xs text-gray-500 mt-4">
                {t("home.deleteServiceWarning")}
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function ListingCard({
  listing,
  onEdit,
  onDelete,
  onManageWorkers,
  userType,
  workers,
}) {
  const { t } = useTranslation();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currencies, setCurrencies] = useState([]);

  const assignedWorkerCount = Array.isArray(listing.workers)
    ? listing.workers.length
    : 0;

  useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        const headers = buildHeaders();

        const res = await fetch(`${API_URL}/service/currencies/`, { headers });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const data = await res.json();
        setCurrencies(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Currency fetch error:", err);
        setCurrencies([]);
      }
    };

    fetchCurrencies();
  }, []);

  const getCurrencyCode = () => {
    const c = listing.currency;

    if (typeof c === "string") return c.toUpperCase();
    if (typeof c === "object" && c?.short_name)
      return c.short_name.toUpperCase();

    return "USD";
  };

  const currencyCode = getCurrencyCode();

  const currencyExistsInBase = currencies.some(
    (c) => c.short_name.toUpperCase() === currencyCode,
  );

  const CurrencyIcon =
    currencyExistsInBase && CURRENCY_ICON_MAP[currencyCode]
      ? CURRENCY_ICON_MAP[currencyCode]
      : FaDollarSign;

  const listingType = listing.listing_type || "Rent";
  const propertyName = listing.property_name || "No Name";
  const propertyDescription = listing.property_description || "No";

  const sellPrice = Number(listing.sell_price || 0);
  const monthlyRentPrice = Number(listing.monthly_rent_price || 0);

  const tourDuration = listing.tour_duration || 120;
  const bufferTime = listing.buffer_time || 30;

  const formatPrice = (amount) => {
    if (!amount && amount !== 0) return "—";

    if (amount >= 1_000_000) {
      const v = amount / 1_000_000;
      return `${v % 1 === 0 ? v.toFixed(0) : v.toFixed(2)}M`;
    }

    if (amount >= 1_000) {
      const v = amount / 1_000;
      return `${v % 1 === 0 ? v.toFixed(0) : v.toFixed(1)}K`;
    }

    return amount.toLocaleString();
  };

  const displayPrice =
    listingType === "Sell"
      ? formatPrice(sellPrice)
      : formatPrice(monthlyRentPrice);

  const handleDeleteClick = () => setShowDeleteModal(true);

  const handleConfirmDelete = () => {
    onDelete(listing.id);
    setShowDeleteModal(false);
  };

  return (
    <>
      <motion.div
        whileHover={{ y: -8, boxShadow: "0 20px 40px rgba(2, 6, 55,0.15)" }}
        transition={{ type: "spring", stiffness: 300, damping: 22 }}
        className="relative bg-white rounded-3xl p-6 border border-gray-100 hover:border-[#020637]/30 flex flex-col h-full justify-between"
      >
        <div>
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1 min-w-0 pr-2">
              <h3
                className="text-xl font-bold text-gray-900 mb-2 line-clamp-1 h-7"
                title={propertyName}
              >
                {propertyName}
              </h3>
              <span className="inline-block text-sm font-semibold text-[#020637] bg-[#eef0f8] px-3 py-1 rounded-full">
                {listingType === "Rent"
                  ? t("home.forRent")
                  : listing.off_plan
                    ? t("home.offPlan")
                    : t("home.forSale")}
              </span>
            </div>

            {userType !== "worker" && (
              <button
                onClick={handleDeleteClick}
                className="text-gray-400 hover:text-red-500 p-2 rounded-lg hover:bg-red-50 flex-shrink-0"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>

          <div className="mb-3">
            <div className="flex items-center">
              <CurrencyIcon className="w-5 h-5 text-gray-900" />
              <span className="text-3xl font-normal text-gray-900">
                {displayPrice}
              </span>
            </div>
          </div>

          <p className="text-sm text-gray-600 line-clamp-3 min-h-[60px] leading-relaxed mb-4">
            {propertyDescription}
          </p>
        </div>

        <div className="relative bg-white rounded-3xl p-6 border border-gray-100 hover:border-[#020637]/30 flex flex-col gap-3 mt-auto">
          <div className="flex justify-between text-sm text-gray-600 bg-gray-50 p-4 rounded-xl">
            <div>
              <p className="font-medium text-gray-900">{t("home.tour")}</p>
              <p>{tourDuration} min</p>
            </div>
            <div>
              <p className="font-medium text-gray-900">{t("home.buffer")}</p>
              <p>{bufferTime} min</p>
            </div>
          </div>
          <div className="flex gap-2">
            {userType === "salon" && onManageWorkers && (
              <button
                onClick={() => onManageWorkers(listing)}
                className={`flex items-center gap-1.5 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  assignedWorkerCount > 0
                    ? "bg-blue-50 text-[#020637] hover:bg-blue-100"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
                title={t("home.addWorkerTitle")}
              >
                <HiOutlineUserGroup className="w-4 h-4" />
                {assignedWorkerCount > 0 && (
                  <span className="text-xs font-bold">
                    {assignedWorkerCount}
                  </span>
                )}
              </button>
            )}
            {userType !== "worker" && (
              <button
                onClick={() => onEdit(listing)}
                className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-[#020637] to-[#1e3aa0] text-white font-semibold text-sm"
              >
                {t("home.editListing")}
              </button>
            )}
          </div>
        </div>
      </motion.div>

      {userType !== "worker" && (
        <DeleteConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleConfirmDelete}
          listingName={propertyName}
        />
      )}
    </>
  );
}

function AddWorkerModal({
  service,
  onClose,
  onUpdated,
  userType,
  errorMessage,
  onRequireServiceCount,
}) {
  const { t } = useTranslation();
  const [workers, setWorkers] = useState([]);
  const [selectedWorkers, setSelectedWorkers] = useState(service.workers || []);
  const [loading, setLoading] = useState(false);

  const token =
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("accessToken");

  useEffect(() => {
    const fetchWorkers = async () => {
      try {
        setLoading(true);
        const headers = buildHeaders();

        const res = await fetch(`${API_URL}/auth/workers-list/`, { headers });
        if (!res.ok) throw new Error("Failed to load workers");
        const data = await res.json();
        setWorkers(
          data.filter((w) => w.full_name && w.full_name.trim() !== ""),
        );
      } catch (err) {
        console.error(err);
        toast.error(t("home.failedLoadWorkers"));
      } finally {
        setLoading(false);
      }
    };
    fetchWorkers();
  }, [token, t]);

  const toggleWorker = (id) => {
    setSelectedWorkers((prev) =>
      prev.includes(id) ? prev.filter((w) => w !== id) : [...prev, id],
    );
  };

  const handleSave = async () => {
    if (selectedWorkers.length === 0) {
      toast.error(t("home.selectWorkerError"));
      return;
    }
    try {
      setLoading(true);

      const headers = buildHeaders();

      const res = await fetch(`${API_URL}/service/my-services/${service.id}/`, {
        method: "PUT",
        headers,
        body: JSON.stringify({
          ...service,
          workers: selectedWorkers,
        }),
      });
      if (!res.ok) throw new Error("Failed to add worker");
      toast.success(t("home.workersAddedSuccess"));

      const toggleRes = await fetch(
        `${API_URL}/service/my-services-toggle-on-off/`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({ service_id: service.id }),
        },
      );
      if (!toggleRes.ok) {
        if (toggleRes.status === 470) {
          onClose();
          onRequireServiceCount?.(service);
          return;
        }
        throw new Error(await toggleRes.text());
      }

      onUpdated();
      onClose();
    } catch (err) {
      console.error(err);
      toast.error(t("home.errorAddWorkerOrActivate"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        key="modal-backdrop"
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          key="modal-content"
          className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 p-6 flex flex-col gap-5"
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{ type: "spring", stiffness: 300, damping: 25 }}
        >
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                {t("home.addWorkerTitle")}
              </h2>
              {service?.name && (
                <p className="text-sm text-gray-500 mt-0.5">{service.name}</p>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {errorMessage && (
            <div className="flex items-center gap-2 p-3 bg-red-50 text-red-600 text-sm rounded-xl border border-red-200">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{errorMessage}</span>
            </div>
          )}

          {loading ? (
            <div className="flex flex-col items-center justify-center py-10">
              <Loader2 className="w-8 h-8 animate-spin text-[#020637] mb-3" />
              <p className="text-gray-500">{t("home.loadingWorkers")}</p>
            </div>
          ) : (
            <div className="flex flex-col gap-3 max-h-80 overflow-y-auto pr-2">
              <AnimatePresence>
                {workers.map((worker) => {
                  const isSelected = selectedWorkers.includes(worker.id);
                  return (
                    <motion.div
                      key={worker.id}
                      layout
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all ${
                        isSelected
                          ? "bg-[#eef0f8] border border-[#020637]/20"
                          : "bg-gray-50 hover:bg-gray-100"
                      }`}
                      onClick={() => toggleWorker(worker.id)}
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <div className="relative">
                          <img
                            src={worker.image_url}
                            alt={worker.full_name}
                            className="w-12 h-12 rounded-full object-cover border-2 border-white shadow-sm"
                          />
                          {isSelected && (
                            <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-[#020637] rounded-full border-2 border-white flex items-center justify-center">
                              <Check className="w-3 h-3 text-white" />
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col flex-1">
                          <span className="font-semibold text-gray-900">
                            {worker.full_name}
                          </span>
                          <span className="text-sm text-gray-500 truncate">
                            {worker.worker_email}
                          </span>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <PrimaryButton
              onClick={onClose}
              variant="outline"
              size="small"
              label={t("home.cancel")}
            />
            <PrimaryButton
              onClick={handleSave}
              size="small"
              label={t("home.linkToService")}
            />
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function ServiceCountModal({ service, onClose, onUpdated, onRequireWorker }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const getInitialState = () => {
    const raw = service?.service_count;
    const str = String(raw ?? "")
      .trim()
      .toLowerCase();
    if (str === "unlimited") {
      return { type: "Unlimited", custom: "" };
    }
    const num = Number(raw);
    if (raw !== null && raw !== undefined && raw !== "" && !isNaN(num)) {
      if (["10", "20"].includes(String(num))) {
        return { type: String(num), custom: "" };
      }
      return { type: "Other", custom: String(num) };
    }
    return { type: "Unlimited", custom: "" };
  };

  const initial = getInitialState();
  const [countType, setCountType] = useState(initial.type);
  const [customCount, setCustomCount] = useState(initial.custom);

  const countOptions = [
    { value: "10", label: "10" },
    { value: "20", label: "20" },
    { value: "Unlimited", label: t("serviceModal.optUnlimited", "Unlimited") },
    { value: "Other", label: t("serviceModal.optOther", "Other") },
  ];

  const handleSave = async () => {
    let finalCount = countType;
    if (countType === "Other") {
      const num = parseInt(customCount, 10);
      if (isNaN(num) || num < 0) {
        toast.error(
          t("serviceModal.placeholderCustomNum", "Please enter a valid count"),
        );
        return;
      }
      finalCount = num;
    }

    try {
      setLoading(true);
      const headers = buildHeaders();
      const res = await fetch(`${API_URL}/service/my-services-update-count/`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          service_id: service.id,
          service_count: finalCount,
        }),
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || "Failed to update service count");
      }

      // If the service is inactive, attempt to toggle/activate it
      if (!service.on_off) {
        const toggleRes = await fetch(
          `${API_URL}/service/my-services-toggle-on-off/`,
          {
            method: "POST",
            headers,
            body: JSON.stringify({ service_id: service.id }),
          },
        );

        if (!toggleRes.ok) {
          if (toggleRes.status === 471) {
            let errorData = null;
            try {
              errorData = await toggleRes.json();
            } catch {}
            onClose();
            onRequireWorker?.(
              service,
              errorData?.error ||
                errorData?.detail ||
                t("home.selectWorkerError"),
            );
            return;
          }
        }
      }

      toast.success(
        t("home.listingUpdatedSuccess", "Service updated successfully"),
      );
      onUpdated();
      onClose();
    } catch (err) {
      console.error(err);
      toast.error(err?.message || t("home.failedToggleService"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        key="count-modal-backdrop"
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          key="count-modal-content"
          className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 p-6 flex flex-col gap-5"
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{ type: "spring", stiffness: 300, damping: 25 }}
        >
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                {t("serviceModal.labelNumServices", "Number of Services")}
              </h2>
              {service?.name && (
                <p className="text-sm text-gray-500 mt-0.5">{service.name}</p>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {countOptions.map((opt) => {
              const isSelected = countType === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setCountType(opt.value)}
                  className={`flex items-center justify-between p-3.5 rounded-xl border text-sm font-semibold transition-all ${
                    isSelected
                      ? "border-[#020637] bg-[#020637]/5 text-[#020637] shadow-sm"
                      : "border-gray-200 bg-gray-50/70 text-gray-700 hover:bg-gray-100 hover:border-gray-300"
                  }`}
                >
                  <span>{opt.label}</span>
                  <div
                    className={`w-5 h-5 rounded-full flex items-center justify-center border transition-all ${
                      isSelected
                        ? "border-[#020637] bg-[#020637] text-white"
                        : "border-gray-300 bg-white"
                    }`}
                  >
                    {isSelected && <Check className="w-3 h-3 stroke-[3]" />}
                  </div>
                </button>
              );
            })}
          </div>

          <AnimatePresence>
            {countType === "Other" && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden"
              >
                <div className="flex flex-col gap-1.5 pt-1">
                  <label className="text-xs font-semibold text-gray-600">
                    {t("serviceModal.placeholderCustomNum", "Enter count")}
                  </label>
                  <input
                    type="number"
                    min="0"
                    placeholder={t(
                      "serviceModal.placeholderCustomNum",
                      "e.g. 15",
                    )}
                    value={customCount}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9]/g, "");
                      setCustomCount(val);
                    }}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-medium focus:outline-none focus:border-[#020637] focus:ring-2 focus:ring-[#020637]/20 transition"
                    autoFocus
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <PrimaryButton
              onClick={onClose}
              variant="outline"
              size="small"
              label={t("home.cancel")}
            />
            <PrimaryButton
              onClick={handleSave}
              disabled={
                loading || (countType === "Other" && customCount === "")
              }
              size="small"
              label={t("home.save", "Save")}
            />
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function AddListingWorkersModal({ listing, onClose, onUpdated }) {
  const { t } = useTranslation();
  const [workers, setWorkers] = useState([]);
  const [selectedWorkers, setSelectedWorkers] = useState(
    Array.isArray(listing.workers)
      ? listing.workers.map((w) => (typeof w === "object" ? w.id : w))
      : [],
  );
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchWorkers = async () => {
      try {
        setLoading(true);
        const headers = buildHeaders();
        const res = await fetch(`${API_URL}/auth/workers-list/`, { headers });
        if (!res.ok) throw new Error("Failed to load workers");
        const data = await res.json();
        setWorkers(
          data.filter((w) => w.full_name && w.full_name.trim() !== ""),
        );
      } catch (err) {
        console.error(err);
        toast.error(t("home.failedLoadWorkers"));
      } finally {
        setLoading(false);
      }
    };
    fetchWorkers();
  }, [t]);

  const toggleWorker = (id) => {
    setSelectedWorkers((prev) =>
      prev.includes(id) ? prev.filter((w) => w !== id) : [...prev, id],
    );
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const headers = buildHeaders();
      const res = await fetch(
        `${API_URL}/service/real-estate-my-listings/${listing.id}/`,
        {
          method: "PATCH",
          headers,
          body: JSON.stringify({ workers: selectedWorkers }),
        },
      );
      if (!res.ok) throw new Error("Failed to update listing workers");
      toast.success(t("home.workersAddedSuccess"));
      onUpdated();
      onClose();
    } catch (err) {
      console.error(err);
      toast.error(t("home.errorAddWorkerOrActivate"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        key="listing-workers-backdrop"
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          key="listing-workers-content"
          className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 p-6 flex flex-col gap-5"
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{ type: "spring", stiffness: 300, damping: 25 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                {t("home.addWorkerTitle")}
              </h2>
              <p className="text-sm text-gray-500 mt-0.5 line-clamp-1">
                {listing.property_name}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition p-1"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {loading ? (
            <div className="flex flex-col items-center justify-center py-10">
              <Loader2 className="w-8 h-8 animate-spin text-[#020637] mb-3" />
              <p className="text-gray-500">{t("home.loadingWorkers")}</p>
            </div>
          ) : workers.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <HiOutlineUserGroup className="w-10 h-10 mx-auto mb-2 opacity-40" />
              <p className="text-sm">
                {t("home.noWorkersAvailable", {
                  defaultValue: "No workers available",
                })}
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-3 max-h-80 overflow-y-auto pr-1">
              <AnimatePresence>
                {workers.map((worker) => {
                  const isSelected = selectedWorkers.includes(worker.id);
                  return (
                    <motion.div
                      key={worker.id}
                      layout
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all ${
                        isSelected
                          ? "bg-[#eef0f8] border border-[#020637]/20"
                          : "bg-gray-50 hover:bg-gray-100"
                      }`}
                      onClick={() => toggleWorker(worker.id)}
                    >
                      <div className="relative">
                        <img
                          src={worker.image_url}
                          alt={worker.full_name}
                          className="w-12 h-12 rounded-full object-cover border-2 border-white shadow-sm"
                        />
                        {isSelected && (
                          <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-[#020637] rounded-full border-2 border-white flex items-center justify-center">
                            <Check className="w-3 h-3 text-white" />
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col flex-1">
                        <span className="font-semibold text-gray-900">
                          {worker.full_name}
                        </span>
                        <span className="text-sm text-gray-500 truncate">
                          {worker.worker_email}
                        </span>
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <PrimaryButton
              onClick={onClose}
              variant="outline"
              size="small"
              label={t("home.cancel")}
            />
            <PrimaryButton
              onClick={handleSave}
              disabled={saving}
              size="small"
              leftIcon={
                saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : undefined
              }
              label={t("home.linkToService")}
            />
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default function YourBusinessMyServices() {
  const { t } = useTranslation();
  const [services, setServices] = useState([]);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingListings, setLoadingListings] = useState(false);
  const [listingFilter, setListingFilter] = useState("all");
  const [showEditModal, setShowEditModal] = useState(false);
  const [showWorkerModal, setShowWorkerModal] = useState(false);
  const [showServiceCountModal, setShowServiceCountModal] = useState(false);
  const [workerModalError, setWorkerModalError] = useState("");
  const [selectedService, setSelectedService] = useState(null);
  const [selectedListing, setSelectedListing] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showWorkersView, setShowWorkersView] = useState(false);
  const [showAddListingModal, setShowAddListingModal] = useState(false);
  const [showEditListingModal, setShowEditListingModal] = useState(false);
  const [showListingWorkersModal, setShowListingWorkersModal] = useState(false);
  const [selectedListingForWorkers, setSelectedListingForWorkers] =
    useState(null);
  const [userType, setUserType] = useState(null);
  const [activeTab, setActiveTab] = useState("services");
  const [showDeleteServiceModal, setShowDeleteServiceModal] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState(null);
  const [workers, setWorkers] = useState([]);
  const industry = useAppSelector(selectIndustryData);
  const isListing = industry?.industry === "Sales & Real Estate";
  const isDealerships = industry?.industry === "Dealerships";
  const token =
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("accessToken");

  const [vehicles, setVehicles] = useState([]);
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const [vehicleFilter, setVehicleFilter] = useState("all");
  const [showVehicleModal, setShowVehicleModal] = useState(false);
  const [selectedVehicleForEdit, setSelectedVehicleForEdit] = useState(null);

  const filteredVehicles = useMemo(() => {
    if (vehicleFilter === "all") return vehicles;
    return vehicles.filter(
      (item) => item.type?.toLowerCase() === vehicleFilter.toLowerCase()
    );
  }, [vehicles, vehicleFilter]);

  const filteredListings = useMemo(() => {
    return listings.filter((listing) => {
      if (listingFilter === "rent") return listing.listing_type === "Rent";
      if (listingFilter === "sale")
        return listing.listing_type === "Sell" && !listing.off_plan;
      if (listingFilter === "off_plan") return !!listing.off_plan;
      return true;
    });
  }, [listings, listingFilter]);

  useEffect(() => {
    const fetchWorkers = async () => {
      if (!token) return;
      try {
        const headers = buildHeaders();
        const res = await fetch(`${API_URL}/auth/workers-list/`, { headers });
        if (res.ok) {
          const data = await res.json();
          setWorkers(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        console.error("Failed to fetch workers list:", err);
      }
    };
    fetchWorkers();
  }, [token]);

  const getServiceWorkerDisplay = (service) => {
    if (!service.workers || service.workers.length === 0) return null;
    const assignedWorkers = service.workers
      .map((wId) => {
        const id = typeof wId === "object" ? wId?.id : wId;
        return workers.find((w) => w.id === id);
      })
      .filter(Boolean);

    if (assignedWorkers.length === 0) return null;
    const firstWorkerName =
      assignedWorkers[0].full_name || t("home.unnamedWorker");
    if (service.workers.length === 1) {
      return firstWorkerName;
    } else {
      return `${firstWorkerName}...`;
    }
  };

  useEffect(() => {
    const fetchUserInfo = async () => {
      if (!token) return;
      try {
        const headers = buildHeaders();

        const res = await fetch(`${API_URL}/auth/userinfo/`, { headers });
        const data = await res.json();
        setUserType(data?.user?.status || null);
      } catch (err) {
        console.error("Failed to fetch user info:", err);
      }
    };
    fetchUserInfo();
  }, [token]);

  const fetchServices = useCallback(async () => {
    try {
      setLoading(true);
      const headers = buildHeaders();

      const res = await fetch(`${API_URL}/service/my-services/`, { headers });
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.detail || "Failed to load services");
      }

      const data = await res.json();
      setServices(
        Array.isArray(data)
          ? data.map((s) => ({ ...s, on_off: s.on_off ?? false }))
          : [],
      );
    } catch (err) {
      toast.error(t("home.failedLoadServices"));
    } finally {
      setLoading(false);
    }
  }, [token, t]);

  const fetchListings = useCallback(async () => {
    try {
      setLoadingListings(true);
      const headers = buildHeaders();

      const res = await fetch(`${API_URL}/service/real-estate-my-listings/`, {
        headers,
      });

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      const data = await res.json();

      const normalizedListings = Array.isArray(data)
        ? data
        : data?.results || data?.data || [];

      setListings(normalizedListings);
    } catch (err) {
      toast.error(t("home.failedLoadListings"));
      setListings([]);
    } finally {
      setLoadingListings(false);
    }
  }, [token, t]);

  const handleDeleteService = async () => {
    if (!serviceToDelete) return;

    try {
      const headers = buildHeaders();

      const res = await fetch(
        `${API_URL}/service/my-services/${serviceToDelete.id}/`,
        {
          method: "DELETE",
          headers,
        },
      );

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || "Failed to delete service");
      }

      toast.success(t("home.serviceDeletedSuccess"));
      fetchServices();
    } catch (err) {
      console.error(err);
      toast.error(t("home.failedDeleteService"));
    } finally {
      setShowDeleteServiceModal(false);
      setServiceToDelete(null);
    }
  };

  const handleDeleteListing = async (listingId) => {
    try {
      const headers = buildHeaders();

      const res = await fetch(
        `${API_URL}/service/real-estate-my-listings/${listingId}/`,
        {
          method: "DELETE",
          headers,
        },
      );

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || "Failed to delete listing");
      }

      toast.success(t("home.listingDeletedSuccess"));
      fetchListings();
    } catch (err) {
      console.error(err);
      toast.error(t("home.failedDeleteListing"));
    }
  };

  const fetchVehicles = useCallback(async () => {
    setLoadingVehicles(true);
    try {
      const res = await getVehicles();
      const data = Array.isArray(res.data) ? res.data : res.data?.results || [];
      setVehicles(data);
    } catch (err) {
      console.error("Failed to fetch vehicles:", err);
      toast.error(t("home.failedLoadVehicles") || "Failed to load vehicles");
    } finally {
      setLoadingVehicles(false);
    }
  }, [t]);

  const handleDeleteVehicle = async (id) => {
    try {
      await deleteVehicle(id);
      toast.success(t("profile.vehicleDeleted") || "Vehicle deleted successfully");
      fetchVehicles();
    } catch (err) {
      console.error("Failed to delete vehicle:", err);
      toast.error(t("home.failedDeleteVehicle") || "Failed to delete vehicle");
    }
  };

  const handleEditVehicle = (vehicleItem) => {
    setSelectedVehicleForEdit(vehicleItem);
    setShowVehicleModal(true);
  };

  const handleCreateVehicle = () => {
    setSelectedVehicleForEdit(null);
    setShowVehicleModal(true);
  };

  useEffect(() => {
    if (token) {
      if (activeTab === "services") {
        fetchServices();
      } else if (activeTab === "listings") {
        fetchListings();
      } else if (activeTab === "dealerships") {
        fetchVehicles();
      }
    }
  }, [token, activeTab, fetchServices, fetchListings, fetchVehicles]);

  const toggleService = async (service) => {
    if (userType === "worker") return;

    try {
      const headers = buildHeaders();

      const res = await fetch(`${API_URL}/service/my-services-toggle-on-off/`, {
        method: "POST",
        headers,
        body: JSON.stringify({ service_id: service.id }),
      });

      if (!res.ok) {
        let errorData = null;
        try {
          errorData = await res.json();
        } catch {
          // not json
        }

        if (res.status === 470) {
          setSelectedService(service);
          setShowServiceCountModal(true);
          return;
        }

        if (res.status === 471) {
          setSelectedService(service);
          setWorkerModalError(
            errorData?.error ||
              errorData?.detail ||
              t("home.selectWorkerError"),
          );
          setShowWorkerModal(true);
          return;
        }

        throw new Error(
          errorData?.error ||
            errorData?.detail ||
            (await res.text()) ||
            "Failed to toggle service",
        );
      }

      const data = await res.json();
      setServices((prev) =>
        prev.map((s) =>
          s.id === service.id ? { ...s, on_off: data.on_off } : s,
        ),
      );
      toast.success(
        t("home.serviceIsNow", {
          status: data.on_off
            ? t("home.statusActive")
            : t("home.statusInactive"),
        }),
      );
    } catch (err) {
      console.error(err);
      toast.error(err?.message || t("home.failedToggleService"));
    }
  };

  const formatServicePrice = (price, currencyCode = "USD") => {
    if (!price) return "—";

    const symbol = getCurrencySymbol(currencyCode);

    if (price >= 1_000_000) {
      const v = price / 1_000_000;
      return `${symbol}${v % 1 === 0 ? v.toFixed(0) : v.toFixed(2)}M`;
    }

    if (price >= 1_000) {
      const v = price / 1_000;
      return `${symbol}${v % 1 === 0 ? v.toFixed(0) : v.toFixed(1)}K`;
    }

    return `${symbol}${price.toLocaleString()}`;
  };

  const handleListingCreated = () => {
    toast.success(t("home.listingCreatedSuccess"));
    fetchListings();
    setShowAddListingModal(false);
  };

  const handleEditListing = (listing) => {
    setSelectedListing(listing);
    setShowEditListingModal(true);
  };

  const handleListingUpdated = () => {
    toast.success(t("home.listingUpdatedSuccess"));
    fetchListings();
    setShowEditListingModal(false);
    setSelectedListing(null);
  };

  

  return (
    <div className="bg-white rounded-2xl shadow-sm p-6 w-full flex flex-col gap-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 mb-2">
        <div className="flex flex-wrap items-center gap-2.5 sm:gap-4 min-w-0">
          {(isListing || isDealerships) && (
            <div className="relative flex items-center bg-gray-100 rounded-2xl p-1 w-fit max-w-full overflow-x-auto no-scrollbar flex-shrink-0">
              <button
                onClick={() => setActiveTab("services")}
                className={`relative z-10 flex items-center gap-2 px-3.5 sm:px-5 py-2 sm:py-2.5 rounded-xl text-sm sm:text-base font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
                  activeTab === "services"
                    ? "text-[#020637]"
                    : "text-gray-600 hover:text-gray-800"
                }`}
              >
                {activeTab === "services" && (
                  <motion.div
                    layoutId="activeBusinessTab"
                    className="absolute inset-0 bg-white rounded-xl shadow-sm z-[-1]"
                    transition={{ type: "spring", stiffness: 400, damping: 30 }}
                  />
                )}
                <Briefcase className="w-4 h-4 flex-shrink-0" />
                {t("home.myServices")}
              </button>
              {isListing && (
                <button
                  onClick={() => {
                    setListingFilter("all");
                    setActiveTab("listings");
                  }}
                  className={`relative z-10 flex items-center gap-2 px-3.5 sm:px-5 py-2 sm:py-2.5 rounded-xl text-sm sm:text-base font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
                    activeTab === "listings"
                      ? "text-[#020637]"
                      : "text-gray-600 hover:text-gray-800"
                  }`}
                >
                  {activeTab === "listings" && (
                    <motion.div
                      layoutId="activeBusinessTab"
                      className="absolute inset-0 bg-white rounded-xl shadow-sm z-[-1]"
                      transition={{ type: "spring", stiffness: 400, damping: 30 }}
                    />
                  )}
                  <Home className="w-4 h-4 flex-shrink-0" />
                  {t("home.myListings")}
                </button>
              )}
              {isDealerships && (
                <button
                  onClick={() => {
                    setVehicleFilter("all");
                    setActiveTab("dealerships");
                  }}
                  className={`relative z-10 flex items-center gap-2 px-3.5 sm:px-4 py-2 sm:py-2.5 rounded-xl text-sm sm:text-base font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
                    activeTab === "dealerships"
                      ? "text-[#020637]"
                      : "text-gray-600 hover:text-gray-800"
                  }`}
                >
                  {activeTab === "dealerships" && (
                    <motion.div
                      layoutId="activeBusinessTab"
                      className="absolute inset-0 bg-white rounded-xl shadow-sm z-[-1]"
                      transition={{ type: "spring", stiffness: 400, damping: 30 }}
                    />
                  )}
                  <Car className="w-4 h-4 flex-shrink-0" />
                  {t("home.myDealerships")}
                </button>
              )}
            </div>
          )}

          {activeTab === "listings" && listings.length > 0 && (
            <div className="flex items-center bg-gray-100 rounded-2xl p-1 w-fit max-w-full overflow-x-auto no-scrollbar flex-shrink-0">
              {[
                { id: "all", label: t("home.allListings") },
                { id: "rent", label: t("home.forRent") },
                { id: "sale", label: t("home.forSale") },
                { id: "off_plan", label: t("home.offPlan") },
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => setListingFilter(item.id)}
                  className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all duration-200 whitespace-nowrap flex-shrink-0 ${
                    listingFilter === item.id
                      ? "bg-white text-[#020637] shadow-sm"
                      : "text-gray-600 hover:text-gray-800"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          )}

          {activeTab === "dealerships" && vehicles.length > 0 && (
            <div className="flex items-center bg-gray-100 rounded-2xl p-1 w-fit max-w-full overflow-x-auto no-scrollbar flex-shrink-0">
              {[
                { id: "all", label: t("profile.filterAll") || "All" },
                { id: "New", label: t("profile.filterNew") || "New" },
                { id: "Pre-owned", label: t("profile.filterPreOwned") || "Pre-owned" },
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => setVehicleFilter(item.id)}
                  className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all duration-200 whitespace-nowrap flex-shrink-0 ${
                    vehicleFilter === item.id
                      ? "bg-white text-[#020637] shadow-sm"
                      : "text-gray-600 hover:text-gray-800"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex-shrink-0 self-start sm:self-auto">
          <AnimatePresence mode="wait">
            {activeTab === "services" &&
              ["salon", "individual"].includes(userType) && (
                <PrimaryButton
                  onClick={() => setShowCreateModal(true)}
                  leftIcon={<Plus className="w-5 h-5" />}
                  label={t("home.createService")}
                />
              )}

            {activeTab === "listings" && userType !== "worker" && (
              <PrimaryButton
                onClick={() => setShowAddListingModal(true)}
                leftIcon={<Plus className="w-5 h-5" />}
                label={t("home.addListing")}
              />
            )}
            {activeTab === "dealerships" && userType !== "worker" && (
              <PrimaryButton
                onClick={handleCreateVehicle}
                leftIcon={<Plus className="w-5 h-5" />}
                label={t("profile.addNewVehicle") || "Add Vehicle"}
              />
            )}
          </AnimatePresence>
        </div>
      </div>

      {activeTab === "services" && (
        <>
          {loading && (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 className="w-8 h-8 text-[#020637] animate-spin mb-3" />
              <p className="text-gray-500">{t("home.loadingServices")}</p>
            </div>
          )}

          {!loading && services.length === 0 && (
            <div className="text-center py-16">
              <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                <Briefcase className="w-10 h-10 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {t("home.noServicesFound")}
              </h3>
              <p className="text-gray-500 mb-6 max-w-sm mx-auto">
                {t("home.getStartedServiceDesc")}
              </p>
              {["salon"].includes(userType) && (
                <PrimaryButton
                  onClick={() => setShowCreateModal(true)}
                  leftIcon={<Plus className="w-4 h-4" />}
                  label={t("home.createFirstServiceBtn")}
                />
              )}
            </div>
          )}
          {!loading && services.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {services.map((service, index) => {
                const serviceCurrencyCode =
                  typeof service.currency === "string"
                    ? service.currency.toUpperCase()
                    : service.currency?.short_name?.toUpperCase() || "USD";

                return (
                  <motion.div
                    key={service.id || index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    whileHover={{
                      y: -4,
                      boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
                    }}
                    className="flex flex-col justify-between bg-white border border-gray-200 rounded-2xl shadow-sm p-5 transition-all hover:border-[#020637]/30"
                  >
                    <div className="flex flex-col gap-3 mb-4">
                      <div className="flex items-start justify-between gap-3">
                        <h4 className="font-bold text-lg text-gray-900 line-clamp-1 flex-1">
                          {service.name}
                        </h4>

                        <div className="flex items-center gap-3">
                          <div
                            className={`w-2.5 h-2.5 rounded-full ${
                              service.on_off ? "bg-green-500" : "bg-gray-300"
                            }`}
                          />

                          {["salon"].includes(userType) && (
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => {
                                setServiceToDelete(service);
                                setShowDeleteServiceModal(true);
                              }}
                              className="text-gray-400 hover:text-red-500 p-2 rounded-lg hover:bg-red-50"
                              title={t("home.deleteServiceTitle")}
                            >
                              <Trash2 className="w-4 h-4" />
                            </motion.button>
                          )}
                        </div>
                      </div>

                      <p className="text-sm text-gray-600 leading-relaxed line-clamp-2 min-h-[40px]">
                        {service.description}
                      </p>
                    </div>

                    <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent mb-4" />

                    <div className="flex items-center justify-between text-sm text-gray-700 mb-5">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-[#eef0f8] flex items-center justify-center">
                          <Tag className="w-3.5 h-3.5 text-[#020637]" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">
                            {t("home.priceLabel")}
                          </p>
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-gray-900">
                              {formatServicePrice(
                                service.price,
                                serviceCurrencyCode,
                              )}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-[#eef0f8] flex items-center justify-center">
                          <Clock className="w-3.5 h-3.5 text-[#020637]" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">
                            {t("home.durationLabel")}
                          </p>
                          <p className="font-semibold text-gray-900">
                            {smartDuration(
                              `${service.duration} ${service.time_unit || "minutes"}`,
                            )}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600 font-medium">
                          {service.on_off
                            ? t("home.statusActive")
                            : t("home.statusInactive")}
                        </span>
                        <motion.button
                          onClick={() => toggleService(service)}
                          className={`w-11 h-6 rounded-full relative ${
                            userType === "worker"
                              ? "cursor-not-allowed opacity-50"
                              : ""
                          }`}
                          disabled={userType === "worker"}
                          initial={false}
                          animate={{
                            backgroundColor: service.on_off
                              ? "#020637"
                              : "#D1D5DB",
                          }}
                          transition={{
                            type: "spring",
                            stiffness: 500,
                            damping: 30,
                          }}
                        >
                          <motion.span
                            className="block w-4 h-4 bg-white rounded-full shadow-md absolute top-1"
                            layout
                            transition={{
                              type: "spring",
                              stiffness: 500,
                              damping: 30,
                            }}
                            style={{
                              left: service.on_off ? "1.625rem" : "0.375rem",
                            }}
                          />
                        </motion.button>
                      </div>

                      {getServiceWorkerDisplay(service) && (
                        <span
                          className="text-xs text-gray-500 font-medium truncate max-w-[120px]"
                          title={
                            service.workers.length > 1
                              ? t("settings.workers")
                              : ""
                          }
                        >
                          {getServiceWorkerDisplay(service)}
                        </span>
                      )}

                      <div className="flex gap-2">
                        {["salon"].includes(userType) && (
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => {
                              setSelectedService(service);
                              setShowWorkersView(true);
                            }}
                            className={`text-sm px-3 py-2 rounded-lg font-medium transition flex items-center gap-1 ${
                              service.workers && service.workers.length > 0
                                ? "bg-blue-50 text-[#020637] hover:bg-blue-100"
                                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                            }`}
                            title={t("home.addWorkerTitle")}
                          >
                            <HiOutlineUserGroup className="w-4 h-4" />
                            {service.workers && service.workers.length > 0 && (
                              <span className="text-xs font-bold">
                                {service.workers.length}
                              </span>
                            )}
                          </motion.button>
                        )}

                        <PrimaryButton
                          onClick={() => {
                            setSelectedService(service);
                            setShowEditModal(true);
                          }}
                          size="small"
                          label={t("home.edit")}
                        />
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </>
      )}

      {activeTab === "listings" && (
        <>
          {loadingListings && (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 className="w-8 h-8 text-[#020637] animate-spin mb-3" />
              <p className="text-gray-500">{t("home.loadingListings")}</p>
            </div>
          )}

          {!loadingListings && listings.length === 0 && (
            <div className="text-center py-16">
              <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                <Home className="w-10 h-10 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {t("home.noListingsFound")}
              </h3>
              <p className="text-gray-500 mb-6 max-w-sm mx-auto">
                {t("home.getStartedListingDesc")}
              </p>
            </div>
          )}

          {!loadingListings && listings.length > 0 && (
            <>
              {filteredListings.length === 0 ? (
                <div className="text-center py-16">
                  <div className="w-16 h-16 rounded-full bg-gray-50 flex items-center justify-center mx-auto mb-4 border border-gray-100">
                    <Home className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-base font-semibold text-gray-900 mb-1">
                    {t("home.noListingsFiltered")}
                  </h3>
                  <PrimaryButton
                    onClick={() => setListingFilter("all")}
                    variant="ghost"
                    size="small"
                    label={t("home.resetFilter")}
                  />
                </div>
              ) : (
                <div className={`grid grid-cols-1 sm:grid-cols-2 ${userType === "salon" ? "2xl:grid-cols-3" : "xl:grid-cols-3"} gap-5`}>
                  {filteredListings.map((listing, index) => (
                    <ListingCard
                      key={listing.id || index}
                      listing={listing}
                      onEdit={handleEditListing}
                      onDelete={handleDeleteListing}
                      onManageWorkers={(l) => {
                        setSelectedListingForWorkers(l);
                        setShowListingWorkersModal(true);
                      }}
                      userType={userType}
                      workers={workers}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </>
      )}

      {activeTab === "dealerships" && (
        <>
          {loadingVehicles && (
            <div className={`grid grid-cols-1 sm:grid-cols-2 ${userType === "salon" ? "2xl:grid-cols-3" : "xl:grid-cols-3"} gap-5`}>
              <VehicleItemSkeleton />
              <VehicleItemSkeleton />
              <VehicleItemSkeleton />
            </div>
          )}

          {!loadingVehicles && vehicles.length === 0 && (
            <div className="text-center py-16">
              <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                <Car className="w-10 h-10 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {t("profile.noVehiclesYet") || "No Vehicles Found"}
              </h3>
              <p className="text-gray-500 mb-6 max-w-sm mx-auto">
                {t("profile.getStartedVehicleDesc") ||
                  "Get started by adding your first vehicle to your dealership inventory."}
              </p>
              {userType !== "worker" && (
                <PrimaryButton
                  onClick={handleCreateVehicle}
                  leftIcon={<Plus className="w-4 h-4" />}
                  label={t("profile.addNewVehicle") || "Add New Vehicle"}
                />
              )}
            </div>
          )}

          {!loadingVehicles && vehicles.length > 0 && (
            <>
              {filteredVehicles.length === 0 ? (
                <div className="text-center py-16">
                  <div className="w-16 h-16 rounded-full bg-gray-50 flex items-center justify-center mx-auto mb-4 border border-gray-100">
                    <Car className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-base font-semibold text-gray-900 mb-1">
                    {t("profile.noVehiclesFiltered") ||
                      "No vehicles match this filter"}
                  </h3>
                  <PrimaryButton
                    onClick={() => setVehicleFilter("all")}
                    variant="ghost"
                    size="small"
                    label={t("home.resetFilter") || "Reset Filter"}
                  />
                </div>
              ) : (
                <div className={`grid grid-cols-1 sm:grid-cols-2 ${userType === "salon" ? "2xl:grid-cols-3" : "xl:grid-cols-3"} gap-5`}>
                  {filteredVehicles.map((vehicleItem, index) => (
                    <VehicleCard
                      key={vehicleItem.id || index}
                      item={vehicleItem}
                      onEdit={handleEditVehicle}
                      onDelete={handleDeleteVehicle}
                      userType={userType}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </>
      )}

      {showEditModal && selectedService && (
        <EditServiceModal
          onClose={() => setShowEditModal(false)}
          service={selectedService}
          onUpdated={() => {
            fetchServices();
            setShowEditModal(false);
          }}
          role={userType}
          allServices={services}
        />
      )}

      {showWorkerModal && selectedService && (
        <AddWorkerModal
          service={selectedService}
          onClose={() => {
            setShowWorkerModal(false);
            setWorkerModalError("");
          }}
          onUpdated={() => {
            fetchServices();
            setShowWorkerModal(false);
            setWorkerModalError("");
          }}
          userType={userType}
          errorMessage={workerModalError}
          onRequireServiceCount={(srv) => {
            setSelectedService(srv);
            setShowServiceCountModal(true);
          }}
        />
      )}

      {showServiceCountModal && selectedService && (
        <ServiceCountModal
          service={selectedService}
          onClose={() => setShowServiceCountModal(false)}
          onUpdated={() => {
            fetchServices();
            setShowServiceCountModal(false);
          }}
          onRequireWorker={(srv, errMsg) => {
            setSelectedService(srv);
            setWorkerModalError(errMsg || "");
            setShowWorkerModal(true);
          }}
        />
      )}

      {showCreateModal && (
        <CreateServiceBussiness
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            fetchServices();
            setShowCreateModal(false);
          }}
        />
      )}

      {showWorkersView && selectedService && (
        <ShowWorkersModal
          service={selectedService}
          onClose={() => setShowWorkersView(false)}
          onUpdated={fetchServices}
        />
      )}

      {showAddListingModal && (
        <AddListingModal
          onClose={() => setShowAddListingModal(false)}
          onCreated={handleListingCreated}
        />
      )}

      {showEditListingModal && selectedListing && (
        <EditListingModal
          listing={selectedListing}
          onClose={() => {
            setShowEditListingModal(false);
            setSelectedListing(null);
          }}
          onUpdated={handleListingUpdated}
        />
      )}

      {showListingWorkersModal && selectedListingForWorkers && (
        <AddListingWorkersModal
          listing={selectedListingForWorkers}
          onClose={() => {
            setShowListingWorkersModal(false);
            setSelectedListingForWorkers(null);
          }}
          onUpdated={() => {
            fetchListings();
            setShowListingWorkersModal(false);
            setSelectedListingForWorkers(null);
          }}
        />
      )}

      {showDeleteServiceModal && serviceToDelete && (
        <DeleteServiceConfirmationModal
          isOpen={showDeleteServiceModal}
          serviceName={serviceToDelete.name}
          onClose={() => {
            setShowDeleteServiceModal(false);
            setServiceToDelete(null);
          }}
          onConfirm={handleDeleteService}
        />
      )}

      {showVehicleModal && (
        <VehicleModal
          vehicle={selectedVehicleForEdit}
          onClose={() => {
            setShowVehicleModal(false);
            setSelectedVehicleForEdit(null);
          }}
          onSaved={() => {
            fetchVehicles();
            setShowVehicleModal(false);
            setSelectedVehicleForEdit(null);
          }}
        />
      )}
    </div>
  );
}
